import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";
import { renderError } from "../lib/errorMessage";

export default function AdminResetPassword() {
  const { t, lang } = useI18n();
  useSEO({ titleKey: "seo.admin_title", descriptionKey: "seo.admin_desc" });
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const logoSrc = lang === "pl" ? "/brand/logo-pl.png" : "/brand/logo-en.jpg";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pwd.length < 8) {
      setError({ key: "admin.password_min_8" });
      return;
    }
    if (pwd !== confirm) {
      setError({ key: "admin.passwords_do_not_match" });
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (
        detail === "Invalid or already-used token" ||
        detail === "Token expired"
      ) {
        setError({ key: "admin.reset_password_invalid" });
      } else if (detail) {
        setError({ text: detail });
      } else {
        setError({ key: "admin.reset_password_invalid" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen grid place-items-center px-5 bg-water-grad"
      data-testid="admin-reset-page"
    >
      <div className="w-full max-w-md rounded-3xl bg-white border border-slate-200 shadow-xl p-8">
        <div className="flex items-center justify-center">
          <img
            src={logoSrc}
            alt="LocalH2O"
            className="h-32 w-auto select-none"
            draggable={false}
          />
        </div>

        {done ? (
          <div className="text-center mt-6" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold tracking-tight">
              {t("admin.reset_password_success")}
            </h2>
            <Link
              to="/admin/login"
              className="mt-6 inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 font-semibold"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t("admin.forgot_password_back")}
            </Link>
          </div>
        ) : !token ? (
          <div className="text-center mt-6" data-testid="reset-no-token">
            <div className="w-14 h-14 rounded-full bg-rose-100 grid place-items-center mx-auto">
              <AlertTriangle className="w-7 h-7 text-rose-600" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold tracking-tight">
              {t("admin.reset_password_invalid")}
            </h2>
            <Link
              to="/admin/login"
              className="mt-6 inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 font-semibold"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t("admin.forgot_password_back")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <div className="mt-2 text-center">
              <h2 className="font-display text-xl font-bold tracking-tight">
                {t("admin.reset_password_title")}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {t("admin.reset_password_subtitle")}
              </p>
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <Label>{t("admin.new_password")}</Label>
                <Input
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  required
                  minLength={8}
                  autoFocus
                  data-testid="reset-new-password"
                />
              </div>
              <div>
                <Label>{t("admin.confirm_new_password")}</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-confirm-password"
                />
              </div>
              {error && (
                <div
                  className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700"
                  data-testid="reset-error"
                >
                  {renderError(error, t)}
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full btn-primary inline-flex items-center justify-center gap-2"
                data-testid="reset-submit"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("admin.reset_password_submit")}
              </button>
              <div className="text-center pt-1">
                <Link
                  to="/admin/login"
                  className="text-sm text-slate-600 hover:text-slate-900 font-semibold inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  {t("admin.forgot_password_back")}
                </Link>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
